/**
 * lib/apiErrors.ts -- a router refusal rendered as what it IS.
 *
 * PURE: no imports. Executed by _test/error-rendering.mjs.
 *
 * A FAILURE MUST NEVER RENDER AS A FACT (router CLAUDE.md section 3).
 *
 * The router answers `{ error: "<CODE>", detail?, code? }` (terminalRoutes
 * err()) or `{ error: "<CODE>", message }` (brokerVaultRoutes err()). The SPA
 * showed the bare code -- or, in the blotters, nothing at all, which reads as
 * "no positions" / "no orders". Several of the codes added in Phase 7 + B6 are
 * specifically "we could not tell" (503) rather than "no": rendering those as
 * a generic error, or worse as an empty list, tells a trader or a compliance
 * officer something false. Each gets a sentence that says what happened and
 * what was (not) done.
 */

export interface RouterErrorBody {
  error?:   unknown;
  code?:    unknown;
  detail?:  unknown;
  message?: unknown;
  required?: unknown;
  mode?:    unknown;
  confirmed_mode?: unknown;
  broker?:  unknown;
}

export type ErrorKind =
  | 'confirm_live'   // 409 TRADING_MODE_MISMATCH: ask the trader to confirm live
  | 'unavailable'    // 503-class: could not determine; NOT a "no"
  | 'not_connected'  // no own-account credentials for this read/order
  | 'refused'        // a definite refusal; nothing was done
  | 'auth'           // session / entitlement
  | 'duplicate'      // 409 duplicate suppressed: the EARLIER order is live
  | 'other';

export interface DescribedError {
  /** The router's code (body.error), when there is one. */
  code:    string | null;
  /** Secondary code (body.code), e.g. OWN_ACCOUNT_REQUIRED under BROKER_NOT_CONNECTED. */
  subcode: string | null;
  kind:    ErrorKind;
  /** A sentence for a human. Never a bare number, never empty. */
  message: string;
}

const s = (v: unknown): string => (typeof v === 'string' ? v : '');

export function describeApiError(status: number, body: RouterErrorBody | null | undefined, statusText = ''): DescribedError {
  const b = (body && typeof body === 'object') ? body : {};
  const code = s(b.error) || null;
  const subcode = s(b.code) || null;
  const detail = (s(b.detail) || s(b.message)).slice(0, 300);
  const out = (kind: ErrorKind, message: string): DescribedError => ({ code, subcode, kind, message });

  switch (code) {
    case 'TRADING_MODE_MISMATCH': {
      const m = s(b.mode).toLowerCase();
      if (m && m !== 'live' && m !== 'unknown') {
        return out('confirm_live',
          `Not sent: this account trades in ${m.toUpperCase()}, not what the ticket showed. `
          + 'Nothing was sent. Re-check the PAPER/LIVE badge and resend.');
      }
      return out('confirm_live',
        'Confirm live trading: this order goes to a REAL-MONEY account and was not confirmed as live. '
        + 'Nothing was sent. Confirm LIVE in the order ticket to send it.');
    }
    case 'DUPLICATE_ORDER_SUPPRESSED':
      return out('duplicate', 'Duplicate suppressed -- NO new order was placed. The earlier order is the live one.');
    case 'BROKER_HEADER_UNSCOPED':
      return out('refused',
        'Refused: a broker credential was sent without naming its broker. Nothing was sent to any broker. '
        + 'This terminal build should never do that -- reload the page.');
    case 'BROKER_NOT_CONNECTED':
      if (subcode === 'OWN_ACCOUNT_REQUIRED') {
        return out('not_connected',
          'No broker account connected: unlock your broker vault. OmniStrat holds no platform brokerage account, '
          + 'so there is nothing to show without your own credentials -- this is NOT an empty account.');
      }
      return out('not_connected', `Broker not connected${s(b.broker) ? ` (${s(b.broker)})` : ''}: ${detail || 'unlock that broker in your vault.'}`);
    case 'DESTINATION_NOT_CONNECTED':
      return out('not_connected', `Not fired: ${detail || 'a destination has no unlocked credentials.'} Nothing was sent to any account.`);
    case 'LEGACY_CREDS_REMOVED':
      return out('refused', 'Refused: this build sent an old credential format. Nothing was sent. Reload the page.');
    case 'ENTITLEMENTS_UNAVAILABLE':
      return out('unavailable',
        'Could not read your plan\'s entitlements right now. Nothing was done. '
        + 'This is not a permissions denial and not an empty result -- retry shortly.');
    case 'ORG_UNRESOLVED':
      return out('unavailable',
        'Could not determine your organisation, so its trading controls cannot be applied. Nothing was done -- retry shortly.');
    case 'DESIGNATIONS_UNAVAILABLE':
      return out('unavailable',
        'Could not read your organisation\'s compliance designations. Nothing was done, and nothing here is '
        + 'an empty result -- retry shortly.');
    case 'DESIGNATION_REQUIRED':
      return out('auth',
        'Only members your organisation has designated as compliance officer may use this workstream. '
        + 'Ask an owner or admin to designate you.');
    case 'SOD_CONFLICT':
      return out('auth', `Segregation of duties: ${detail || 'you hold an entitlement that conflicts with this compliance role.'}`);
    case 'MODULE_REQUIRED':
      return out('auth', detail || 'Your organisation does not have this module.');
    case 'CAT_PORTAL_URL_REFUSED':
      return out('refused',
        'Refused: the CAT portal URL is not an allowed destination. Nothing was filed. Check the portal URL configured for CAT.');
    case 'AUDIT_UNAVAILABLE':
      return out('unavailable', `The audit ledger could not record this, so it was not done. ${detail || 'Nothing was sent; retry shortly.'}`);
    case 'KILL_STATE_UNAVAILABLE':
      return out('unavailable', 'Could not verify whether trading is halted for your organisation, so the order was refused. Nothing was sent.');
    case 'TRADING_HALTED':
    case 'KILL_SWITCH_ACTIVE':
      return out('refused', `Trading is halted${detail ? `: ${detail}` : ''}. Nothing was sent.`);
    case 'TOKEN_IN_URL_REFUSED':
      return out('refused', 'Live feed refused a session token in its URL. Reload the page (this build connects with a single-use ticket).');
    case 'WS_TICKET_USED':
      return out('auth', 'Live-feed ticket already used; a new one is required.');
    case 'WS_TICKET_UNVERIFIABLE':
    case 'WS_TICKET_UNAVAILABLE':
      return out('unavailable', 'Live-feed sign-in is temporarily unavailable; retrying.');
    case 'UNAUTHORISED':
      return out('auth', 'Your session has expired or you are not signed in. Sign in again.');
    case 'FORBIDDEN':
      return out('auth', s(b.required) ? `Your plan does not include ${s(b.required)}.` : (detail || 'Not authorised.'));
    default:
      break;
  }

  // Unknown code: keep the code AND the detail (the detail says WHY).
  if (code) {
    return out(status >= 500 ? 'unavailable' : 'other',
      detail && detail !== code ? `${code} (${detail.slice(0, 200)})` : code);
  }
  if (detail) return out(status >= 500 ? 'unavailable' : 'other', detail);
  if (status === 401) return out('auth', 'Your session has expired or you are not signed in. Sign in again.');
  if (status === 403) return out('auth', 'Not authorised.');
  return out(status >= 500 ? 'unavailable' : 'other', `Request failed (HTTP ${status}${statusText ? ` ${statusText}` : ''}).`);
}

/**
 * Blotter bodies. The router answers a plain array when every broker was
 * read, and `{ rows, degraded, partial: true }` when one was not
 * (terminalRoutes blotterBody). The SPA typed both as an array: the partial
 * shape crashed the positions table, and an unexpected shape treated as `[]`
 * would read as "flat". Unknown shapes THROW -- an unreadable blotter is an
 * error, not an empty one.
 */
export interface Blotter<T> {
  rows:     T[];
  degraded: Array<{ broker: string; error: string }>;
}
export function readBlotter<T>(body: unknown): Blotter<T> {
  if (Array.isArray(body)) return { rows: body as T[], degraded: [] };
  const o = body as { rows?: unknown; degraded?: unknown } | null;
  if (o && typeof o === 'object' && Array.isArray(o.rows)) {
    const degraded = Array.isArray(o.degraded)
      ? (o.degraded as Array<{ broker?: unknown; error?: unknown }>).map((d) => ({ broker: s(d?.broker) || '?', error: s(d?.error) || 'unreadable' }))
      : [];
    return { rows: o.rows as T[], degraded };
  }
  throw new Error('The blotter response had an unexpected shape -- it was NOT read as empty.');
}
